import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAmendments } from 'actions/expiryRenewalActions';
import { MemberAddAmendment } from 'types/expiryRenewal';
import { Store } from 'types/store';
import { toIsoDateOfBirth } from '../utils/amendmentUtils';
import useMemberForm, { emptyMemberFields } from './useMemberForm';

export type { MemberFields, MemberFieldErrors, TouchedMemberFields } from './useMemberForm';
export {
  MIN_DATE_OF_BIRTH,
  NAME_MAX_LENGTH,
  TITLE_OPTIONS,
  validateDateOfBirth,
  validateMemberFields,
  validateName,
} from './useMemberForm';

/**
 * Stages a new member as an ExtraPerson/Add amendment. The form state itself lives in
 * useMemberForm, shared with useEditMember - the same split vehicles use.
 */
const useAddMember = (onClose: () => void) => {
  const dispatch = useDispatch();
  const pageModel = useSelector((state: Store) => state.expiryRenewal.pageModel);
  const existingAmendments = useSelector((state: Store) => state.expiryRenewal.amendments);

  const { fields, errors, touched, hasErrors, setTouched, handleChange, handleBlur } = useMemberForm(emptyMemberFields);

  const isSaveDisabled = hasErrors;

  const handleSave = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ Title: true, FirstName: true, LastName: true, DateOfBirth: true });
    if (hasErrors || !pageModel) return;

    const newAmendment: MemberAddAmendment = {
      productCode: 'ExtraPerson',
      amendmentType: 'Add',
      memberAdd: {
        title: fields.Title,
        firstName: fields.FirstName.trim(),
        surname: fields.LastName.trim(),
        dateOfBirth: toIsoDateOfBirth(fields.DateOfBirth),
      },
    };

    dispatch(setAmendments([...existingAmendments, newAmendment]));
    onClose();
  }, [fields, hasErrors, pageModel, existingAmendments, dispatch, onClose, setTouched]);

  return { fields, errors, touched, isSaveDisabled, handleChange, handleBlur, handleSave };
};

export default useAddMember;
